"use client";

import {CardBody, CardFooter, CardHeader} from "@heroui/card";
import Form from "next/form";
import {Input} from "@heroui/input";
import {Button} from "@heroui/button";
import {Alert} from "@heroui/alert";
import {EyeIcon, EyeSlashIcon} from "@heroicons/react/24/solid";
import {Suspense, useActionState, useState} from "react";
import {useTranslations} from "next-intl";

import {CallbackUrlInput} from "./CallbackUrlInput";

import {Image} from "@/lib/components/ui/image";
import {Link} from "@/lib/components/ui/link";
import {signInAction} from "@/lib/actions";
import {Card} from "@/lib/components/ui/card";

export const SignInForm = () => {
  const [{errors, message} = {errors: {}}, formAction, pending] = useActionState(signInAction, {
    errors: {},
    message: undefined,
  });

  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => setIsVisible(!isVisible);

  const t = useTranslations("LoginPage");

  return (
    <>
      <Card className="sm:p-6 lg:p-8">
        <CardHeader className="mb-6">
          <Image
            alt="OVision logo"
            className="sm:mx-auto h-10 w-[162px]"
            height={40}
            loading="eager"
            src="/images/logo.svg"
            width={162}
          />
        </CardHeader>
        <CardBody>
          <Form action={formAction} className="space-y-4">
            <Suspense fallback={<input name="callbackUrl" type="hidden" />}>
              <CallbackUrlInput />
            </Suspense>
            <Input
              fullWidth
              isRequired
              aria-label={t("username.label")}
              errorMessage={({validationDetails}) => {
                if (validationDetails.valueMissing) {
                  return t("username.errors.required");
                } else if (validationDetails.typeMismatch) {
                  return t("username.errors.invalid");
                } else {
                  return (
                    <ul>
                      {errors?.username?.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  );
                }
              }}
              label={t("username.label")}
              name="username"
              placeholder={t("username.placeholder")}
              size="lg"
              type="email"
            />

            <Input
              fullWidth
              isRequired
              aria-label={t("password.label")}
              endContent={
                <button
                  aria-label="toggle password visibility"
                  className="focus:outline-solid outline-transparent cursor-pointer"
                  type="button"
                  onClick={toggleVisibility}
                >
                  {isVisible ? (
                    <EyeSlashIcon className="size-6 text-default-400 pointer-events-none" />
                  ) : (
                    <EyeIcon className="size-6 text-default-400 pointer-events-none" />
                  )}
                </button>
              }
              errorMessage={({validationDetails}) => {
                if (validationDetails.valueMissing) {
                  return t("password.errors.required");
                } else if (validationDetails.tooShort) {
                  return t("password.errors.min");
                } else if (validationDetails.tooLong) {
                  return t("password.errors.max");
                } else {
                  return (
                    <ul>
                      {errors?.username?.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  );
                }
              }}
              label={t("password.label")}
              maxLength={32}
              minLength={8}
              name="password"
              placeholder={t("password.placeholder")}
              size="lg"
              type={isVisible ? "text" : "password"}
            />
            <Button fullWidth color="primary" isLoading={pending} type="submit">
              {t("submitButton")}
            </Button>
          </Form>
        </CardBody>
        <CardFooter>
          <Link href="/forgot-password">{t("forgotPassword")}</Link>
        </CardFooter>
      </Card>
      {message && (
        <Alert
          className="mt-8 sm:mt-14"
          color="danger"
          description={t("loginErrorDescription")}
          radius="md"
          title={t("loginErrorTitle")}
          variant="solid"
        />
      )}
    </>
  );
};
